import React from 'react';
import { useTranslation } from '../context/TranslationContext';
import './CompletedRequestDetailModal.css';

interface CompletedRequestDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  request: any;
}

const CompletedRequestDetailModal: React.FC<CompletedRequestDetailModalProps> = ({
  isOpen,
  onClose,
  request
}) => {
  const { t } = useTranslation();

  if (!isOpen || !request) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currency || 'EUR'
    }).format(amount);
  };

  return (
    <div className="completed-request-modal-overlay" onClick={onClose}>
      <div className="completed-request-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('common.completed_request_details')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          {/* Basic Information */}
          <div className="info-section">
            <h3>{t('common.basic_information')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('common.request_id')}:</label>
                <span>{request.specific_id || request._id}</span>
              </div>
              <div className="info-item">
                <label>{t('common.product_name')}:</label>
                <span>{request.product_name}</span>
              </div>
              <div className="info-item">
                <label>{t('common.product_brand')}:</label>
                <span>{request.product_brand || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>{t('common.product_category')}:</label>
                <span>{request.product_category}</span>
              </div>
              <div className="info-item">
                <label>{t('common.requester')}:</label>
                <span>{request.user_name}</span>
              </div>
              <div className="info-item">
                <label>{t('common.company')}:</label>
                <span>{request.company_name || 'Independent User'}</span>
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="info-section">
            <h3>{t('common.financial_information')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('common.budget')}:</label>
                <span>{formatCurrency(request.budget, request.currency)}</span>
              </div>
              <div className="info-item">
                <label>{t('common.currency')}:</label>
                <span>{request.currency}</span>
              </div>
              <div className="info-item">
                <label>{t('common.delivery_time')}:</label>
                <span>{request.delivery_time || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>{t('common.minimum_order')}:</label>
                <span>{request.minimum_order || 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>{t('common.payment_terms')}:</label>
                <span>{request.payment_terms || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* OCR Information */}
          {request.ocr_data && (
            <div className="info-section">
              <h3>{t('common.ocr_information')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('common.ocr_unit_price')}:</label>
                  <span>{formatCurrency(request.ocr_unit_price, request.ocr_currency)}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.ocr_total_price')}:</label>
                  <span>{formatCurrency(request.ocr_total_price, request.ocr_currency)}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.ocr_budget')}:</label>
                  <span>{formatCurrency(request.ocr_budget, request.ocr_currency)}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.ocr_purchase_date')}:</label>
                  <span>{request.ocr_purchase_date ? formatDate(request.ocr_purchase_date) : 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Supplier Information */}
          {request.ocr_supplier_name && (
            <div className="info-section">
              <h3>{t('common.supplier_information')}</h3>
              <div className="info-grid">
                <div className="info-item">
                  <label>{t('common.supplier_name')}:</label>
                  <span>{request.ocr_supplier_name}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.supplier_email')}:</label>
                  <span>{request.ocr_supplier_email || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.supplier_phone')}:</label>
                  <span>{request.ocr_supplier_phone || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.supplier_address')}:</label>
                  <span>{request.ocr_supplier_address || 'N/A'}</span>
                </div>
                <div className="info-item">
                  <label>{t('common.supplier_website')}:</label>
                  <span>{request.ocr_supplier_website || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Product History */}
          {request.product_history && request.product_history.length > 0 && (
            <div className="info-section">
              <h3>{t('common.product_history')}</h3>
              <div className="history-timeline">
                {request.product_history.map((history: any, index: number) => (
                  <div key={index} className="history-item">
                    <div className="history-date">
                      {formatDate(history.timestamp)}
                    </div>
                    <div className="history-content">
                      <div className="history-changes">
                        <div className="change-item">
                          <label>{t('common.product_name')}:</label>
                          <span>{history.product_name}</span>
                        </div>
                        <div className="change-item">
                          <label>{t('common.product_brand')}:</label>
                          <span>{history.product_brand || 'N/A'}</span>
                        </div>
                        <div className="change-item">
                          <label>{t('common.price')}:</label>
                          <span>{formatCurrency(history.price, history.currency)}</span>
                        </div>
                        <div className="change-item">
                          <label>{t('common.delivery_time')}:</label>
                          <span>{history.delivery_time || 'N/A'}</span>
                        </div>
                        {history.notes && (
                          <div className="change-item">
                            <label>{t('common.notes')}:</label>
                            <span>{history.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Additional Information */}
          <div className="info-section">
            <h3>{t('common.additional_information')}</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>{t('common.created_at')}:</label>
                <span>{formatDate(request.created_at)}</span>
              </div>
              <div className="info-item">
                <label>{t('common.updated_at')}:</label>
                <span>{formatDate(request.updated_at)}</span>
              </div>
              <div className="info-item">
                <label>{t('common.completed_at')}:</label>
                <span>{request.completed_at ? formatDate(request.completed_at) : 'N/A'}</span>
              </div>
              <div className="info-item">
                <label>{t('common.status')}:</label>
                <span className="status-badge completed">{request.status}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          {request.additional_notes && (
            <div className="info-section">
              <h3>{t('common.notes')}</h3>
              <div className="notes-content">
                {request.additional_notes}
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="close-button" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompletedRequestDetailModal;
